"use client";

import { usePathname, useRouter } from "next/navigation";
import Nwea from "@/components/Nwea";

/** Pages where the floating banner should NOT appear */
export default function WorkbookFloatingBanner() {
  const pathname = usePathname();
  const router = useRouter();

  // Disabled site-wide — landing has its own quick-action grid in
  // src/app/page.tsx Hero. To restore, change the sentinel back to "/".
  if (pathname !== "__disabled__") return null;

  return (
    <div className="fixed top-[92px] lg:top-[72px] left-0 right-0 z-40 bg-[#0a1628] border-b border-[#1a3050] overflow-x-hidden">
      <div className="max-w-7xl mx-auto px-2 md:px-4 py-2 lg:pt-[21px] lg:pb-3.5">
        {/* Tablet (≥640px) and below stack vertically: at iPad portrait
            (768px) the three cards' whitespace-nowrap labels and right-side
            badges sum to >1000px, which overflowed the viewport horizontally.
            Three-across layout only kicks in at lg: (1024px+). */}
        <div className="flex flex-col lg:flex-row lg:justify-center gap-2 lg:gap-4">
          {/* Card 1: 실전 MAP TEST 연습하기 (moved to first slot — primary action) */}
          <button
            onClick={() => router.push("/signup")}
            className="inline-flex items-center gap-2 lg:gap-4 w-full lg:w-fit py-1 px-3 lg:py-2 lg:px-5 rounded-lg lg:rounded-xl bg-white shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
          >
            <div className="w-7 h-7 min-w-[28px] lg:w-9 lg:h-9 lg:min-w-[36px] rounded-md lg:rounded-xl bg-[#f5f2e8] flex items-center justify-center shrink-0">
              <svg
                viewBox="0 0 24 24" fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="shrink-0 w-4 h-4 lg:w-5 lg:h-5"
              >
                <path
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
                  stroke="#c9a84c"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
            </div>
            <div className="text-[12px] lg:text-[18px] font-bold text-[#0d1b35] text-left lg:text-center whitespace-nowrap leading-tight min-w-0 flex-1 lg:flex-initial">
              <Nwea /> MAP TEST 모의고사 응시
            </div>
            <span className="shrink-0 ml-auto bg-[#0d1b35] text-white text-[10px] lg:text-[11px] font-semibold px-2 py-0.5 lg:px-3 lg:py-1.5 rounded-md whitespace-nowrap">
              연습하기
            </span>
          </button>

          {/* Card 2: 워크북 구매 */}
          <button
            onClick={() => router.push("/workbook")}
            className="inline-flex items-center gap-2 lg:gap-4 w-full lg:w-fit py-1 px-3 lg:py-2 lg:px-5 rounded-lg lg:rounded-xl bg-white shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
          >
            <div className="w-7 h-7 min-w-[28px] lg:w-9 lg:h-9 lg:min-w-[36px] rounded-md lg:rounded-xl bg-[#f5f2e8] flex items-center justify-center shrink-0">
              <svg
                viewBox="0 0 24 24" fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="shrink-0 w-4 h-4 lg:w-5 lg:h-5"
              >
                <rect x="3" y="3" width="13" height="18" rx="2" fill="#c9a84c" opacity="0.18"/>
                <rect x="3" y="3" width="13" height="18" rx="2" stroke="#c9a84c" strokeWidth="1.5"/>
                <path d="M10 8h4M10 11h4M10 14h3" stroke="#c9a84c" strokeWidth="1.3" strokeLinecap="round"/>
                <circle cx="19" cy="17" r="4" fill="#c9a84c"/>
                <path d="M17.5 17l1 1 2-2" stroke="#0a1628" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="text-[12px] lg:text-[18px] font-bold text-[#0d1b35] text-left lg:text-center whitespace-nowrap leading-tight min-w-0 flex-1 lg:flex-initial">
              개별 맞춤형 MAP TEST 워크북 구매
            </div>
            <span className="shrink-0 ml-auto bg-[#0d1b35] text-white text-[10px] lg:text-[11px] font-semibold px-2 py-0.5 lg:px-3 lg:py-1.5 rounded-md whitespace-nowrap">
              구매하기
            </span>
          </button>

          {/* Card 3: 온라인 응시 */}
          <button
            onClick={() => router.push("/map-test")}
            className="inline-flex items-center gap-2 lg:gap-4 w-full lg:w-fit py-1 px-3 lg:py-2 lg:px-5 rounded-lg lg:rounded-xl bg-white shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
          >
            <div className="w-7 h-7 min-w-[28px] lg:w-9 lg:h-9 lg:min-w-[36px] rounded-md lg:rounded-xl bg-[#f5f2e8] flex items-center justify-center shrink-0">
              <svg
                viewBox="0 0 24 24" fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="shrink-0 w-4 h-4 lg:w-5 lg:h-5"
              >
                <rect x="2" y="4" width="20" height="14" rx="2" stroke="#7a9abb" strokeWidth="1.5"/>
                <path d="M8 22h8M12 18v4" stroke="#7a9abb" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M9 11l2 2 4-4" stroke="#7a9abb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="text-[12px] lg:text-[18px] font-bold text-[#0d1b35] text-left lg:text-center whitespace-nowrap leading-tight min-w-0 flex-1 lg:flex-initial">
              공식 <Nwea /> MAP TEST 온라인 응시
            </div>
            <span className="shrink-0 ml-auto bg-[#0d1b35] text-white text-[10px] lg:text-[11px] font-semibold px-2 py-0.5 lg:px-3 lg:py-1.5 rounded-md whitespace-nowrap">
              응시하기
            </span>
          </button>
        </div>

      </div>
    </div>
  );
}
